import { type FC, useState, useMemo, useEffect } from 'react'
import { DataTable } from '@/components/data-table/DataTable'
import { userColumns } from '@/components/users/UserColumns'
import {
    type User,
    useUsers,
    useFlagUser,
    useUnflagUser,
} from '@/hooks/useUsers'
import { unflagUser, flagUser } from '@/services/users.service'
import {
    useDownloadUserTransactionHistory,
    useDownloadUsersTransactionHistory,
} from '@/hooks/useTransactions'
import { AccountInformationModal } from '@/components/users/AccountInformationModal'
import { FlagAccountDialog } from '@/components/users/FlagAccountDialog'
import { UsersFilterDialog } from '@/components/users/UsersFilterDialog'
import {
    Download,
    Eye,
    Flag,
    FlagOff,
    AlertCircle,
    RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import type { UserFilters } from '@/types/user'

const Users: FC = () => {
    const [selectedUser, setSelectedUser] = useState<User | null>(null)
    const [selectedUsersForFlag, setSelectedUsersForFlag] = useState<User[]>([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isFlagDialogOpen, setIsFlagDialogOpen] = useState(false)
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false)

    const [filters, setFilters] = useState<UserFilters>({
        page: 1,
        limit: 20,
    })

    const { data: usersData, isLoading, error, refetch } = useUsers(filters)
    const flagMutation = useFlagUser()
    const unflagMutation = useUnflagUser()
    const downloadUserHistory = useDownloadUserTransactionHistory()
    const downloadUsersHistory = useDownloadUsersTransactionHistory()

    useEffect(() => {
        if (error) {
            toast.error('Failed to load users', {
                description:
                    error instanceof Error
                        ? error.message
                        : 'An unexpected error occurred',
                action: {
                    label: 'Retry',
                    onClick: () => refetch(),
                },
            })
        }
    }, [error, refetch])

    const handleRetry = () => {
        refetch()
    }

    const users = useMemo<User[]>(() => {
        if (!usersData?.data?.data || !Array.isArray(usersData.data.data))
            return []

        return usersData.data.data.map(user => ({
            id: user.userId,
            accountName: user.accountName,
            walletAddress: user.walletAddress,
            email: user.email,
            phoneNo: user.phoneNumber,
            dateJoined: new Date(user.joinedDate).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
            }),
            gender:
                user.gender === 'MALE'
                    ? 'M'
                    : user.gender === 'FEMALE'
                      ? 'F'
                      : (user.gender as 'M' | 'F'),
            status:
                user.status === 'ACTIVE'
                    ? 'Active'
                    : user.status === 'FLAGGED'
                      ? 'Flagged'
                      : 'Active',

            userId: user.userId,
            phoneNumber: user.phoneNumber,
            dateOfBirth: '',
            countryOfResidence: '',
            residentialAddress: '',
            totalTransactions: 0,
            amountSent: 0,
            amountReceived: 0,
            amountBought: 0,
            amountSold: 0,
        }))
    }, [usersData])

    const handleViewUser = (user: User) => {
        setSelectedUser(user)
        setIsModalOpen(true)
    }

    const handleDownloadHistory = (selectedUsers: User[]) => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected')
            return
        }

        if (selectedUsers.length === 1) {
            downloadUserHistory.mutate(
                { userId: selectedUsers[0].userId },
                {
                    onSuccess: () =>
                        toast.success(
                            'Transaction history downloaded successfully'
                        ),
                    onError: () =>
                        toast.error('Failed to download transaction history'),
                }
            )
        } else {
            const toastId = toast.loading(
                `Downloading transaction history for ${selectedUsers.length} users...`
            )
            downloadUsersHistory.mutate(
                { userIds: selectedUsers.map(u => u.userId) },
                {
                    onSuccess: () => {
                        toast.dismiss(toastId)
                        toast.success(
                            `Transaction history for ${selectedUsers.length} users downloaded successfully`
                        )
                    },
                    onError: () => {
                        toast.dismiss(toastId)
                        toast.error('Failed to download transaction history')
                    },
                }
            )
        }
    }

    const handleFlagAccount = (selectedUsers: User[]) => {
        if (selectedUsers.length === 0) return

        const shouldUnflag = selectedUsers.every(u => u.status === 'Flagged')

        if (shouldUnflag) {
            const toastId = toast.loading(
                `Unflagging ${selectedUsers.length} user(s)...`
            )

            Promise.all(
                selectedUsers.map(user =>
                    unflagUser(user.userId)
                        .then(() => ({ success: true, user }))
                        .catch(error => ({ success: false, user, error }))
                )
            ).then(results => {
                toast.dismiss(toastId)

                const successful = results.filter(r => r.success)
                const failed = results.filter(r => !r.success)

                if (successful.length > 0) {
                    toast.success(
                        `Unflagged ${successful.length} user(s) successfully`
                    )
                }

                if (failed.length > 0) {
                    toast.error(`Failed to unflag ${failed.length} user(s)`)
                }

                refetch()
            })
        } else {
            setSelectedUsersForFlag(selectedUsers)
            setIsFlagDialogOpen(true)
        }
    }

    const handleFlagAccountSingle = () => {
        if (!selectedUser) return

        const shouldFlag = selectedUser.status === 'Active'

        if (shouldFlag) {
            setSelectedUsersForFlag([selectedUser])
            setIsFlagDialogOpen(true)
        } else {
            unflagMutation.mutate(selectedUser.userId, {
                onSuccess: () => {
                    toast.success(`Unflagged ${selectedUser.accountName}`)
                    setIsModalOpen(false)
                },
                onError: error => {
                    toast.error(`Failed to unflag user`, {
                        description:
                            error instanceof Error
                                ? error.message
                                : 'An error occurred',
                    })
                },
            })
        }
    }

    const handleConfirmFlag = async (reason: string) => {
        if (selectedUsersForFlag.length === 0) return

        const toastId = toast.loading(
            `Flagging ${selectedUsersForFlag.length} user(s)...`
        )

        try {
            const results = await Promise.all(
                selectedUsersForFlag.map(user =>
                    flagUser(user.userId, { reason })
                        .then(() => ({ success: true, user }))
                        .catch(error => ({ success: false, user, error }))
                )
            )

            toast.dismiss(toastId)

            const successful = results.filter(r => r.success)
            const failed = results.filter(r => !r.success)

            if (successful.length > 0) {
                toast.success(
                    `Flagged ${successful.length} user(s) successfully`
                )
            }

            if (failed.length > 0) {
                toast.error(`Failed to flag ${failed.length} user(s)`, {
                    description: failed.map(f => f.user.accountName).join(', '),
                })
            }

            setIsFlagDialogOpen(false)
            setIsModalOpen(false)
            setSelectedUsersForFlag([])
            refetch()
        } catch (error) {
            toast.dismiss(toastId)
            toast.error('Failed to flag users', {
                description:
                    error instanceof Error
                        ? error.message
                        : 'An error occurred',
            })
        }
    }

    const handleApplyFilters = (newFilters: UserFilters) => {
        setFilters({ ...newFilters, page: 1, limit: filters.limit })
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold">Users</h1>
                    <p className="text-sm text-muted-foreground">
                        Manage all user accounts on Ogaboss
                    </p>
                </div>
                <div className="flex items-center justify-center h-[400px]">
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <div className="rounded-full bg-red-100 p-3">
                                <AlertCircle className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold">
                                Failed to load users
                            </h3>
                            <p className="text-sm text-muted-foreground max-w-md">
                                {error instanceof Error
                                    ? error.message
                                    : 'An unexpected error occurred while fetching users.'}
                            </p>
                        </div>
                        <Button
                            onClick={handleRetry}
                            variant="outline"
                            className="gap-2"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Retry
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-2xl font-semibold">Users</h1>
                <p className="text-sm text-muted-foreground">
                    Manage all user accounts on Ogaboss
                </p>
            </div>

            {/* Users Table */}
            {isLoading ? (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Skeleton className="h-10 w-[300px]" />
                        <Skeleton className="h-10 w-[100px]" />
                    </div>
                    <div className="rounded-md border">
                        <Skeleton className="h-12 w-full rounded-t-md" />
                        {Array.from({ length: 10 }).map((_, i) => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))}
                    </div>
                    <div className="flex items-center justify-between">
                        <Skeleton className="h-8 w-[200px]" />
                        <Skeleton className="h-8 w-[300px]" />
                    </div>
                </div>
            ) : (
                <DataTable
                    columns={userColumns}
                    data={users}
                    searchPlaceholder="Search using user ID, username & wallet address"
                    onViewRow={handleViewUser}
                    onFilter={() => setIsFilterDialogOpen(true)}
                    showViewButton={false}
                    customActions={[
                        {
                            label: 'Download transaction history',
                            icon: (
                                <Download className="h-4 w-4" color="#FB3748" />
                            ),
                            onClick: handleDownloadHistory,
                        },
                        {
                            label: selected =>
                                selected.length > 0 &&
                                selected.every(u => u.status === 'Flagged')
                                    ? 'Un-flag account'
                                    : 'Flag account',
                            icon: selected =>
                                selected.length > 0 &&
                                selected.every(u => u.status === 'Flagged') ? (
                                    <FlagOff
                                        className="h-4 w-4"
                                        color="#1FC16B"
                                    />
                                ) : (
                                    <Flag className="h-4 w-4" color="#FB3748" />
                                ),
                            onClick: handleFlagAccount,
                        },
                        {
                            label: 'View account',
                            icon: <Eye className="h-4 w-4" color="#17A2B8" />,
                            onClick: selectedUsers => {
                                if (selectedUsers.length === 1) {
                                    handleViewUser(selectedUsers[0])
                                }
                            },
                            showForSingle: true,
                        },
                    ]}
                />
            )}

            {/* Account Information Modal */}
            <AccountInformationModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                user={selectedUser}
                onFlagAccount={handleFlagAccountSingle}
            />

            {/* Flag Account Dialog */}
            <FlagAccountDialog
                open={isFlagDialogOpen}
                onOpenChange={setIsFlagDialogOpen}
                accountName={
                    selectedUsersForFlag.length > 0
                        ? selectedUsersForFlag[0].accountName
                        : selectedUser?.accountName || ''
                }
                accountCount={selectedUsersForFlag.length}
                onConfirm={handleConfirmFlag}
                isLoading={flagMutation.isPending}
            />

            {/* Users Filter Dialog */}
            <UsersFilterDialog
                open={isFilterDialogOpen}
                onOpenChange={setIsFilterDialogOpen}
                currentFilters={filters}
                onApplyFilters={handleApplyFilters}
            />
        </div>
    )
}

export default Users
